import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useInView, useScroll, useTransform, AnimatePresence } from "framer-motion";
import { Ruler, Gem, Layers, ShieldCheck, ArrowLeft, ArrowRight, Star } from "lucide-react";
import { Page, FadeUp, SectionHead, MaskedLines } from "@/components/Reveal";
import DenimBand from "@/components/denim/DenimBand";
import ZipCTA from "@/components/denim/ZipCTA";
import Newsletter from "@/components/home/Newsletter";

const PILLARS = [
    { icon: Ruler, title: "Data-Driven Fit", copy: "Engineered through strict mathematical patterning for a flawless, natural silhouette." },
    { icon: Gem, title: "Quiet Luxury", copy: "High-grade, performance textiles chosen to outlast fast-fashion cycles." },
    { icon: Layers, title: "Structural Innovation", copy: "Small-batch construction with heavy-duty, reinforced stitching." },
    { icon: ShieldCheck, title: "Lifetime Warranty", copy: "Fully protected against structural failures and unexpected tears for life." },
];

const METRICS = [
    { label: "Fabric Durability", val: 95 },
    { label: "Structural Integrity", val: 85 },
    { label: "Fit Precision", val: 90 },
];

const CRAFT = [
    { img: "/img/urwan/Urwan_01_15.jpg", label: "Stretch Denim", sub: "Stretch-woven, performance-first fabrics" },
    { img: "/img/urwan/2J7A0685.jpg", label: "Reinforced Seams", sub: "Stress-tested at every stress point" },
    { img: "/img/urwan/BD0A8494.jpg", label: "Slow Fashion", sub: "Made slowly, worn longest" },
    { img: "/img/urwan/2J7A0775.jpg", label: "Lifetime Promise", sub: "Every seam covered, for life" },
    { img: "/img/urwan/DSC00614.jpg", label: "Small Batch", sub: "Limited runs, exclusive by design" },
];

const TESTIMONIALS = [
    { quote: "Stylish collection and a wonderful shopping experience from start to finish, every single time.", name: "Jessica Smith" },
    { quote: "The fit is immaculate — a denim that finally understands long workdays and Indian summers.", name: "Wade Warren" },
    { quote: "Quiet luxury in every stitch. My selvedge pair has aged beautifully over a full year of wear.", name: "Michael Brown" },
];

function Metric({ label, val }) {
    const ref = useRef(null);
    const inView = useInView(ref, { once: true, amount: 0.5 });
    const [n, setN] = useState(0);
    useEffect(() => {
        if (!inView) return;
        let cur = 0;
        const id = setInterval(() => {
            cur += 1;
            if (cur >= val) {
                cur = val;
                clearInterval(id);
            }
            setN(cur);
        }, 14);
        return () => clearInterval(id);
    }, [inView, val]);
    return (
        <div ref={ref} data-testid={`metric-${label.toLowerCase().replace(/\s+/g, "-")}`}>
            <div className="flex items-end justify-between">
                <p className="text-[11px] font-mono uppercase tracking-[0.25em] text-mute">{label}</p>
                <p className="font-display text-3xl font-bold text-gold tabular-nums">{n}%</p>
            </div>
            <div className="mt-3 h-[3px] w-full bg-white/10">
                <motion.div
                    className="h-full bg-gold"
                    initial={{ width: 0 }}
                    whileInView={{ width: `${val}%` }}
                    viewport={{ once: true, amount: 0.5 }}
                    transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
                />
            </div>
        </div>
    );
}

function Testimonials() {
    const [i, setI] = useState(0);
    const t = TESTIMONIALS[i];
    const go = (d) => setI((i + d + TESTIMONIALS.length) % TESTIMONIALS.length);
    return (
        <div data-testid="testimonial-slider" className="border border-white/10 bg-coal/60 p-8 md:p-12">
            <Star size={28} className="text-gold" fill="currentColor" />
            <AnimatePresence mode="wait">
                <motion.blockquote
                    key={i}
                    initial={{ opacity: 0, x: 36 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -36 }}
                    transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
                >
                    <p className="mt-6 font-display text-2xl font-bold leading-snug text-ink md:text-3xl">
                        “{t.quote}”
                    </p>
                    <footer className="mt-7 flex items-center gap-4">
                        <div className="grid h-12 w-12 shrink-0 place-items-center rounded-full border border-gold/40 bg-elev font-display text-sm font-bold text-gold">
                            {t.name.split(" ").map((w) => w[0]).join("")}
                        </div>
                        <div>
                            <p className="font-display text-sm font-bold uppercase tracking-wide text-ink">
                                {t.name}
                            </p>
                            <p className="text-xs text-mute">Verified Buyer</p>
                        </div>
                    </footer>
                </motion.blockquote>
            </AnimatePresence>
            <div className="mt-8 flex items-center justify-between border-t border-white/10 pt-6">
                <p className="font-mono text-xs tracking-[0.2em] text-mute">
                    {String(i + 1).padStart(2, "0")} / {String(TESTIMONIALS.length).padStart(2, "0")}
                </p>
                <div className="flex gap-3">
                    <button
                        data-testid="testimonial-prev"
                        onClick={() => go(-1)}
                        aria-label="Previous testimonial"
                        className="grid h-11 w-11 place-items-center border border-white/15 text-ink transition-colors hover:border-gold hover:text-gold"
                    >
                        <ArrowLeft size={16} />
                    </button>
                    <button
                        data-testid="testimonial-next"
                        onClick={() => go(1)}
                        aria-label="Next testimonial"
                        className="grid h-11 w-11 place-items-center border border-white/15 text-ink transition-colors hover:border-gold hover:text-gold"
                    >
                        <ArrowRight size={16} />
                    </button>
                </div>
            </div>
        </div>
    );
}

export default function About() {
    const { scrollY } = useScroll();
    const yImg = useTransform(scrollY, [0, 900], [0, -70]);

    return (
        <Page>
            <section className="mx-auto w-full max-w-[1400px] px-5 pb-16 pt-36 md:px-8 md:pt-44 lg:px-10">
                <p className="flex items-center gap-3 text-[11px] font-mono uppercase tracking-[0.3em] text-gold">
                    <span className="inline-block h-px w-10 bg-gold" /> About Us
                </p>
                <h1 className="mt-5 font-display text-5xl font-extrabold uppercase leading-[0.95] tracking-tight sm:text-6xl">
                    <MaskedLines lines={["The House of", "Urwan."]} />
                </h1>
                <motion.p
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                    className="mt-7 max-w-2xl text-base leading-relaxed text-mute md:text-lg"
                >
                    URWAN exists outside the fast-fashion cycle. We cut performance denim in small
                    batches, pattern it by data, and back every seam with a lifetime promise —
                    menswear made to be worn hard and kept for years.
                </motion.p>
            </section>

            <section className="mx-auto w-full max-w-[1400px] px-5 md:px-8 lg:px-10">
                <FadeUp>
                    <div className="relative h-[46vh] overflow-hidden border border-white/10 md:h-[62vh]">
                        <motion.img
                            style={{ y: yImg }}
                            src="/img/urwan/Asset-11-scaled.png"
                            alt="URWAN campaign — two models in blue denim"
                            className="absolute inset-0 h-[115%] w-full object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-obsidian/70 via-transparent to-transparent" />
                    </div>
                </FadeUp>
            </section>

            <section className="mx-auto w-full max-w-[1400px] px-5 py-24 md:px-8 md:py-32 lg:px-10">
                <SectionHead eyebrow="What We Stand For" title="Four Promises, One Standard" />
                <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4 lg:items-stretch">
                    {PILLARS.map((p, i) => (
                        <FadeUp key={p.title} delay={i * 0.08} className="h-full">
                            <div className="group h-full border border-white/10 bg-coal/60 p-7 transition-colors duration-300 hover:border-gold/50">
                                <span className="grid h-12 w-12 place-items-center rounded-full border border-gold/40 text-gold transition-colors duration-300 group-hover:bg-gold group-hover:text-black">
                                    <p.icon size={20} />
                                </span>
                                <h3 className="mt-6 font-display text-lg font-bold uppercase leading-tight text-ink">
                                    {p.title}
                                </h3>
                                <p className="mt-3 text-sm leading-relaxed text-mute">{p.copy}</p>
                            </div>
                        </FadeUp>
                    ))}
                </div>
            </section>

            <DenimBand className="py-24 md:py-32" testid="metrics-band">
                <div className="mx-auto grid w-full max-w-[1400px] items-center gap-14 px-5 md:px-8 lg:grid-cols-2 lg:px-10">
                    <div>
                        <SectionHead
                            eyebrow="Our Metrics"
                            title="Measured, Not Promised"
                            sub="We measure our garments by performance, structural innovation, and engineering precision — not seasonal trends."
                        />
                    </div>
                    <div className="space-y-9">
                        {METRICS.map((m, i) => (
                            <FadeUp key={m.label} delay={i * 0.1}>
                                <Metric {...m} />
                            </FadeUp>
                        ))}
                    </div>
                </div>
            </DenimBand>

            <section className="mx-auto w-full max-w-[1400px] px-5 py-24 md:px-8 md:py-32 lg:px-10">
                <div className="grid items-stretch gap-12 lg:grid-cols-2">
                    <div className="flex flex-col justify-center">
                        <SectionHead eyebrow="Testimonial" title="Worn & Vouched For" />
                        <p className="mt-4 max-w-md text-sm leading-relaxed text-mute">
                            Real words from people who wear URWAN every day.
                        </p>
                    </div>
                    <Testimonials />
                </div>
            </section>

            <section className="border-t border-white/10 bg-coal/40 py-24 md:py-32">
                <div className="mx-auto w-full max-w-[1400px] px-5 md:px-8 lg:px-10">
                    <SectionHead eyebrow="Inside the Atelier" title="Built, Not Bought" />
                </div>
                <FadeUp delay={0.1}>
                    <div className="mt-12 flex snap-x gap-5 overflow-x-auto px-5 pb-4 md:px-8 lg:px-10">
                        {CRAFT.map((c) => (
                            <div
                                key={c.label}
                                className="group w-60 shrink-0 snap-start md:w-64"
                                data-testid={`craft-tile-${c.label.toLowerCase().replace(/\s+/g, "-")}`}
                            >
                                <div className="aspect-square overflow-hidden bg-elev">
                                    <img
                                        src={c.img}
                                        alt={c.label}
                                        loading="lazy"
                                        className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                                    />
                                </div>
                                <h3 className="mt-4 font-display text-base font-bold uppercase text-ink">
                                    {c.label}
                                </h3>
                                <p className="mt-1 text-xs leading-relaxed text-mute">{c.sub}</p>
                            </div>
                        ))}
                    </div>
                </FadeUp>
            </section>

            <section className="mx-auto w-full max-w-[1400px] px-5 py-24 text-center md:px-8 md:py-32 lg:px-10">
                <FadeUp>
                    <p className="text-[11px] font-mono uppercase tracking-[0.3em] text-gold">Designed to Last</p>
                    <h2 className="mx-auto mt-4 max-w-3xl font-display text-3xl font-bold uppercase leading-tight tracking-tight text-ink md:text-5xl">
                        Reject the Fast-Fashion Cycle
                    </h2>
                    <Link
                        to="/shop"
                        data-testid="about-shop-cta"
                        className="mt-9 inline-block bg-gold px-9 py-4 font-display text-sm font-bold uppercase tracking-[0.2em] text-black transition-colors hover:bg-ink"
                    >
                        Shop the Collection
                    </Link>
                </FadeUp>
            </section>

            <Newsletter />
        </Page>
    );
}
